import {
  CcfBox,
  CcfChatIcon,
  CcfEmailIcon,
  CcfPhoneInboundIcon,
  CcfSmsIcon,
  CcfTypography,
  useTranslator,
} from '@nice-ccf/ui-controls';
import styles from './ccf-consumer-card-interactions-details-view.module.scss';
import { Divider, IconButton } from '@mui/material';
import { useCallback, useState } from 'react';
import {
  Close,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  KeyboardDoubleArrowDown,
  KeyboardDoubleArrowUp,
} from '@mui/icons-material';
import EmailInteractionFlow from './email-interaction-flow/email-interaction-flow';
import VoiceTranscriptionFlow from './voice-transcription-flow/voice-transcription-flow';
import { useSelector } from 'react-redux';
import { activeContactSelector } from '../../../../ccf-inbox/ccf-inbox.slice';
import ChatInteractionFlow from './chat-interaction-flow/chat-interaction-flow';

/* eslint-disable-next-line */
export interface CcfConsumerCardInteractionsDetailsViewProps {
  interaction: any;
  interactionViewSelectHandler: (view: string) => void;
  previousView: string;
}

/**
 * CcfConsumerCardInteractionsDetailsView - returns Interactions Tab Details View
 * @param props - CcfConsumerCardInteractionsDetailsViewProps
 * @returns - appspace
 * @example - `<CcfConsumerCardInteractionsDetailsView />`
 */
export function CcfConsumerCardInteractionsDetailsView(
  props: CcfConsumerCardInteractionsDetailsViewProps
) {
  const { interaction, interactionViewSelectHandler, previousView } = props;
  const [showDetails, setShowDetails] = useState(true);
  const activeContact = useSelector(activeContactSelector);
  const [translate] = useTranslator();

  /**
   * Function to select interaction view
   * @example handleInteractionView()
   */
  const handleInteractionView = useCallback(
    (selectedView: string) => {
      interactionViewSelectHandler(selectedView);
    },
    [interactionViewSelectHandler]
  );

  /**
   * Function to render Icon for Channel
   * @example renderIconForChannel()
   */
  const renderIconForChannel = (channel: string) => {
    switch (channel) {
      case 'Voice':
        return <CcfPhoneInboundIcon className={styles.channelIcon} />;
      case 'Email':
        return <CcfEmailIcon className={styles.channelIcon} />;
      case 'SMS':
        return <CcfSmsIcon className={styles.channelIcon} />;
      case 'Chat':
        return <CcfChatIcon className={styles.channelIcon} />;
      default:
        return null;
    }
  };

  /**
   * Used to format contact history date and time
   * @param time -string
   * @example - formatDateTimeHandler()
   */
  const formatDateTimeHandler = (time: string) => {
    const date = new Date(time);

    return date.toLocaleDateString('en-US', {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /**
   * Function to convert the timeformat to 'hh:mm:ss a' format
   * @example convertTimeFormatHandler()
   */
  const convertTimeFormatHandler = (timeString: string): string => {
    const date = new Date(timeString);

    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';

    const seconds = date.getSeconds();

    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    const secondsStr = seconds < 10 ? '0' + seconds : seconds;

    const formattedTime = `${hours}:${minutesStr}:${secondsStr} ${ampm}`;

    return formattedTime;
  };

  return (
    <CcfBox className={styles.interactionDetails}>
      <CcfBox className={styles.interactionDetailsHeader}>
        <CcfBox display={'flex'} gap={'10px'}>
          <CcfBox>{renderIconForChannel(interaction.media)}</CcfBox>
          <CcfBox>
            <CcfBox>
              <CcfTypography className={styles.contactIdTxt}>
                {interaction.media === 'Email'
                  ? activeContact?.customer.emailId
                  : activeContact?.customer.phoneNo}
              </CcfTypography>
            </CcfBox>
            <CcfBox display={'flex'} gap={'10px'} alignItems={'center'}>
              <CcfTypography className={styles.text}>
                {formatDateTimeHandler(interaction.date)}
              </CcfTypography>
              <CcfTypography className={styles.text}>|</CcfTypography>
              <CcfTypography className={styles.text}>
                #{' '}
                <span style={{ color: '#0B82DD' }}>
                  {' '}
                  {interaction.interactionId}
                </span>
              </CcfTypography>
            </CcfBox>
            <CcfBox>
              <CcfTypography
                style={{
                  color:
                    interaction.status.toLowerCase() === 'open'
                      ? '#008940'
                      : '#5f6367',
                  fontWeight: 'bold',
                }}
                className={styles.text}
                translationKey={interaction.status.toLowerCase()}
              />
            </CcfBox>
          </CcfBox>
        </CcfBox>
        <CcfBox className={styles.btnActions}>
          <IconButton disabled>
            <KeyboardArrowLeft className={styles.btnArrowIcon} />
          </IconButton>
          <IconButton disabled>
            <KeyboardArrowRight className={styles.btnArrowIcon} />
          </IconButton>
          <IconButton>
            <Close
              className={styles.closeIcon}
              onClick={() => handleInteractionView(previousView)}
            />
          </IconButton>
        </CcfBox>
      </CcfBox>
      <Divider />
      <CcfBox className={styles.interactionDetailsPanel}>
        <CcfBox className={styles.detailsWrapper}>
          {showDetails && (
            <>
              <CcfBox className={styles.details}>
                <CcfBox className={styles.textBlock}>
                  <CcfBox>
                    <DetailItem
                      title={translate('start')}
                      value={convertTimeFormatHandler(interaction.date)}
                    />
                  </CcfBox>
                  <CcfBox>
                    <DetailItem
                      title={translate('Agent')}
                      value={interaction.agentName}
                    />
                  </CcfBox>
                  <CcfBox>
                    <DetailItem
                      title={translate('campaign')}
                      value={interaction.campaign}
                    />
                  </CcfBox>
                </CcfBox>
                <CcfBox className={styles.textBlock}>
                  <CcfBox>
                    <DetailItem
                      title={translate('end')}
                      value={interaction.endTime}
                    />
                  </CcfBox>
                  <CcfBox>
                    <DetailItem
                      title={translate('skill')}
                      value={interaction.skill}
                    />
                  </CcfBox>
                  <CcfBox>
                    <DetailItem
                      title={translate('disposition')}
                      value={interaction.disposition}
                    />
                  </CcfBox>
                </CcfBox>
              </CcfBox>
              <CcfBox className={styles.autoSummary}>
                <DetailItem
                  title={translate('autoSummary')}
                  value={interaction.autoSummary}
                />
              </CcfBox>
              {interaction.media === 'Voice' && (
                <img
                  alt="Voice Player"
                  src="./assets/voiceTranscriptPlayer.png"
                />
              )}

              <Divider />
            </>
          )}

          <CcfBox
            className={styles.showHideDetailsBlock}
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? (
              <KeyboardDoubleArrowUp className={styles.arrowIcon} />
            ) : (
              <KeyboardDoubleArrowDown className={styles.arrowIcon} />
            )}

            <CcfTypography className={styles.showHideText}>
              {showDetails ? translate('hide') : translate('show')}{' '}
              {translate('details')}
            </CcfTypography>
          </CcfBox>
        </CcfBox>
      </CcfBox>
      <Divider />
      {interaction.media === 'Email' ? (
        <EmailInteractionFlow interaction={interaction} />
      ) : interaction.media === 'Voice' ? (
        <VoiceTranscriptionFlow interaction={interaction} />
      ) : interaction.media === 'SMS' || interaction.media === 'Chat' ? (
        <ChatInteractionFlow interaction={interaction} />
      ) : null}
    </CcfBox>
  );
}

export default CcfConsumerCardInteractionsDetailsView;

/**
 * DetailItem - returns detail item View
 * @returns - appspace
 * @example - `<DetailItem />`
 */
const DetailItem = ({ title, value }: { title: string; value: string }) => (
  <CcfBox>
    <CcfTypography className={styles.title}>{title}</CcfTypography>
    <CcfTypography className={styles.value}>{value}</CcfTypography>
  </CcfBox>
);
